import { HvlFormConstants, HvlEnumGridColumn, HvlAbstractOperationalForm } from '@hvlng/framework-core/form';
import { Directive, Injector, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { LookupModel } from 'infrastructure-shared';
import { logComponent } from '../hr-logger';
import { HrFormButtonEvents } from './hr-form-button-events';
import { HrFormToolbarEvents } from './hr-form-toolbar-events';
import { HrFormLoadingVisibility } from './hr-form-loading-visibility';
import {
    getFormTitleAsync,
    getFormTitleSync,
    getPageCode,
    goMasterData,
    translateAsync,
} from '../page-code/page-code-func';
import { PageCode } from '../page-code/page-code';
import { PageCodeModel } from '../page-code/page-code-model';
import { Router } from '@angular/router';
import { EnumHrmType } from '../../module/hrm-type/model/enum-hrm-type';
import { HvlLanguageService } from '@hvlng/framework-core/language';
import { TranslateService } from '@ngx-translate/core';
import { UiConfirmationDialogService, UiToastMessageService } from 'ui-shared';
import { ServiceCall } from '../service/service-call';
import { PrimeNGConfig } from 'primeng/api';
import { HrBaseParam } from './hr-base-param';
import { HrResourceName } from '../model/hr-resource-name';
import { HrResourceLoader } from '../service/hr-resource-loader';
import { HrTranslateKey } from '../translate/hr-translate-key';

@Directive()
export abstract class HrBaseFormPage
    extends HvlAbstractOperationalForm
    implements HrFormToolbarEvents, HrFormButtonEvents, HrFormLoadingVisibility, OnDestroy
{
    private _pageCode: string;
    private _pageTitle: string;
    private _pageCodeModel: PageCodeModel;
    private _listTitle: string;
    private _searchTitle: string;
    private _contentVisible = false;

    private resourceLoader: HrResourceLoader;
    protected translateService: TranslateService;
    public toastMessage: UiToastMessageService;
    public serviceCall: ServiceCall;
    public confirmationDialogService: UiConfirmationDialogService;
    public config: PrimeNGConfig;
    public langService: HvlLanguageService;
    public router?: Router;
    peopleSubscription: Subscription;

    protected constructor(pageCode: PageCode, injector: Injector, params?: HrBaseParam) {
        super();
        logComponent(this);
        this.initVariables(pageCode, injector, params);
        this.initResources();
    }

    private initVariables(pageCode: PageCode, injector: Injector, params?: HrBaseParam) {
        this._pageCodeModel = new PageCodeModel(pageCode);
        this.translateService = injector.get(TranslateService);
        this.toastMessage = injector.get(UiToastMessageService);
        this.serviceCall = injector.get(ServiceCall);
        this.confirmationDialogService = injector.get(UiConfirmationDialogService);
        this.langService = injector.get(HvlLanguageService);
        this.config = injector.get(PrimeNGConfig);
        this.resourceLoader = injector.get(HrResourceLoader);
        if (params?.hasRouter) {
            this.router = injector.get(Router);
        }
    }

    private initResources() {
        this.resourceLoader.init(this.getResourceList());
    }

    public abstract getResourceList(): Array<HrResourceName>;

    public abstract openAddForm();

    public abstract openCopyForm();

    public abstract openEditForm();

    public abstract openRemoveForm();

    public abstract openViewForm();

    public abstract onCancel();

    public abstract onSave();

    public abstract onRemove();

    public openForm(
        formMode: HvlFormConstants,
        formTitle: string,
        formColSize: HvlEnumGridColumn = HvlEnumGridColumn.COL12,
    ): void {
        this.setPageCode(formMode);
        super.openForm(formMode, formTitle, formColSize);
    }

    private setPageCode(formMode: HvlFormConstants) {
        this._pageCode = getPageCode(formMode, this.pageCodeModel);
    }

    get pageCode(): string {
        return this._pageCode;
    }

    get pageTitle(): string {
        return this._pageTitle;
    }

    get pageCodeModel(): PageCodeModel {
        return this._pageCodeModel;
    }

    public setPageTitle(title: string) {
        this._pageTitle = title;
    }

    get listTitle(): string {
        return this._listTitle;
    }

    set listTitle(value: string) {
        this._listTitle = value;
    }

    get searchTitle(): string {
        return this._searchTitle;
    }

    set searchTitle(value: string) {
        this._searchTitle = value;
    }

    get contentVisible(): boolean {
        return this._contentVisible;
    }

    public showContent() {
        this._contentVisible = true;
    }

    public showLoading() {}

    public hideLoading() {}

    /**
     * @param hrmTypeList: codetextservisten gelen liste
     *
     * @returns [0] isHrmTypeDisabled boolean value.
     *
     *
     */
    private async isHrmTypeNone(hrmTypeList: Observable<LookupModel[]>): Promise<any> {
        return new Promise<{ disabled: boolean; hrmTypeUuid: string }>((resolve) => {
            hrmTypeList.subscribe((value) => {
                if (value.length === 1 && value[0].code === EnumHrmType.NONE) {
                    resolve({ disabled: true, hrmTypeUuid: value[0].uuid });
                } else {
                    resolve({ disabled: false, hrmTypeUuid: null });
                }
            });
        });
    }

    public async setHrmTypeUuidAndReturn(
        form: UntypedFormGroup,
        hrmTypeList: Observable<Array<LookupModel>>,
    ): Promise<boolean> {
        const { disabled, hrmTypeUuid } = await this.isHrmTypeNone(hrmTypeList);
        if (disabled) {
            form.setVal('hrmTypeUuid', hrmTypeUuid);
        }
        return disabled;
    }

    public goMasterData() {
        goMasterData(this.router);
    }

    public get formModeEnum(): typeof HvlFormConstants {
        return HvlFormConstants;
    }

    public get hrKey(): typeof HrTranslateKey {
        return HrTranslateKey;
    }

    public translateSync(key: string): string | any {
        return this.translateService.instant(key);
    }

    public translateAsync(key: string): Promise<string | any> {
        return translateAsync(this.translateService, key);
    }

    public async getFormTitleAsyncAsSearch(pageTitle: string): Promise<string | any> {
        return getFormTitleAsync(this.translateService, pageTitle, null);
    }

    public async getFormTitleAsync(pageTitle: string, formModeParam?: HvlFormConstants): Promise<string | any> {
        return getFormTitleAsync(
            this.translateService,
            pageTitle,
            formModeParam != null ? formModeParam : this.formMode,
        );
    }

    public getFormTitleSyncAsSearch(pageTitle: string): string | any {
        return getFormTitleSync(this.translateService, pageTitle, null);
    }

    public getFormTitleSync(pageTitle: string, formModeParam?: HvlFormConstants): string | any {
        return getFormTitleSync(
            this.translateService,
            pageTitle,
            formModeParam != null ? formModeParam : this.formMode,
        );
    }

    public closeForm(): void {
        super.closeForm();
        this.formMode = HvlFormConstants.LIST;
    }

    ngOnDestroy() {
        if (this.peopleSubscription) this.peopleSubscription.unsubscribe();
        super.ngOnDestroy();
    }
}
